import Vue from 'vue';
import Router from 'vue-router';
import baseUrlHelper from '@/helpers/baseUrlHelper';

Vue.use(Router);

const defaultLayoutName = 'default';
const loginLayoutName = 'login';
const purchaseOrderLayoutName = 'purchase-order';
const purchaseOrderShippingLabelLayoutName = 'purchase-order-shipping-label';
const usersManagementLayoutName = 'users-management';
const workloadLayoutName = 'workload';

function loadView(view) {
  return () => import(/* webpackChunkName: "view-[request]" */ `@/views/${view}.vue`);
}

function loadComponent(path, component) {
  return () => import(/* webpackChunkName: "component-[request]" */ `@/components/${path}/${component}.vue`);
}

export default new Router({
  mode: 'history',
  base: baseUrlHelper.baseUrl,
  routes: [
    {
      path: '/',
      name: 'start',
      meta: {
        requiresAuth: true,
        layout: defaultLayoutName,
      },
      component: () => import('./views/Start.vue'),
    },
    {
      path: '/purchase-orders/:purchaseOrderNumber/details/:sku?',
      name: 'purchaseOrderDetails',
      meta: {
        requiresAuth: true,
        layout: purchaseOrderLayoutName,
      },
      component: () => import('./views/PurchaseOrderDetails.vue'),
      props: true,
    },
    {
      path: '/purchase-orders/:purchaseOrderNumber/label/:sku?',
      name: 'purchaseOrderShippingLabel',
      meta: {
        requiresAuth: true,
        layout: purchaseOrderShippingLabelLayoutName,
      },
      component: () => import('./views/PurchaseOrderShippingLabel.vue'),
      props: true,
    },
    {
      path: '/register',
      name: 'register',
      meta: {
        layout: loginLayoutName,
      },
      component: () => import('./views/Register.vue'),
    },
    {
      path: '/reset/password/request',
      name: 'resetPasswordRequest',
      meta: {
        layout: loginLayoutName,
      },
      component: () => import('./views/ResetPasswordRequest.vue'),
    },
    {
      path: '/reset/password/:resetPasswordToken',
      name: 'resetPassword',
      meta: {
        layout: loginLayoutName,
      },
      component: () => import('./views/ResetPassword.vue'),
    },
    {
      path: '/register/emailConfirmation/:emailConfirmationToken',
      name: 'emailConfirmation',
      meta: {
        layout: loginLayoutName,
      },
      component: () => import('./views/ConfirmEmail.vue'),
    },
    {
      path: '/login',
      name: 'login',
      meta: {
        layout: loginLayoutName,
      },
      component: () => import('./views/Login.vue'),
    },
    {
      path: '/users',
      redirect: 'users/employees',
      name: 'users',
      meta: {
        requiresAuth: true,
        layout: usersManagementLayoutName,
      },
      component: () => import('./views/Users.vue'),
      children: [
        {
          meta: {
            requiresAuth: true,
            layout: usersManagementLayoutName,
            headerTitle: 'Users Management: Suppliers',
          },
          path: '/users/suppliers',
          component: () => import('./components/UsersManagement/Suppliers/SuppliersManagement.vue'),
        },
        {
          meta: {
            requiresAuth: true,
            layout: usersManagementLayoutName,
            headerTitle: 'Users Management: Employees',
          },
          path: '/users/employees',
          component: () => import('./components/UsersManagement/Employees/EmployeesManagement.vue'),
        },
      ],
    },
    {
      path: '/purchase-orders/list/assigned',
      name: 'mylist',
      meta: {
        requiresAuth: true,
        layout: defaultLayoutName,
      },
      component: () => import('./views/MyPurchaseOrders.vue'),
    },
    {
      path: '/subordinates/workload',
      name: 'workload',
      meta: {
        requiresAuth: true,
        layout: workloadLayoutName,
      },
      components: {
        default: loadView('SubordinatesWorkload'),
        sidebar: loadComponent('SubordinatesWorkload', 'SideBar'),
      },
      props: {
        default: { name: 'Home' },
        sidebar: (route) => {
          const clients = route.query.clients || [];

          return {
            pageNumber: parseInt(route.query.pageNumber || 1, 10),
            pageSize: parseInt(route.query.pageSize || 10, 10),
            filter: route.query.filter,
            clients: Array.isArray(clients) ? clients : [clients],
          };
        },
      },
    },
    {
      path: '/guide/theme',
      name: 'guide',
      meta: {
        layout: loginLayoutName,
      },
      component: () => import('./views/GuideTheme.vue'),
    },
    {
      path: '*',
      name: 'notFound',
      meta: {
        requiresAuth: false,
        layout: loginLayoutName,
      },
      component: () => import('./views/404.vue'),
    },
    {
      path: '/lookup',
      name: 'orderSearchLookup',
      meta: {
        requiresAuth: true,
        layout: defaultLayoutName,
      },
      component: () => import('./views/SupplierSearchPurchaseOrder.vue'),
    },
    {
      path: '/search',
      name: 'purchaseOrderSearch',
      meta: {
        requiresAuth: true,
        layout: defaultLayoutName,
      },
      component: () => import('./views/SearchPurchaseOrder.vue'),
    },
    {
      path: '/purchase-orders/:purchaseOrderNumber/print-label/:sku?',
      name: 'printLabel',
      meta: {
        requiresAuth: true,
        noLayout: true,
      },
      component: () => import('./views/PrintLabel.vue'),
    },
    {
      path: '/purchase-orders/:purchaseOrderNumber/print-mixed-label/:sku?',
      name: 'printLabelMixed',
      meta: {
        requiresAuth: true,
        noLayout: true,
      },
      component: () => import('./views/PrintLabelMixed.vue'),
    },
  ],
});
